import React, { useRef, useState } from "react"
import countryList from './countryList'
import Recaptcha from "react-google-recaptcha"
import { useFormik, FormikProvider, Form, useField, Field } from 'formik'
import * as Yup from 'yup'

const API_PATH=`${process.env.REACT_APP_CONFIG_BASEURL}`

const LiveFeedbackField = ({ ...props }) => {
  const [field, meta] = useField(props);

  // Show inline feedback if EITHER
  // - the input is focused AND value is longer than 2 characters
  // - or, the has been visited (touched === true)
  const [didFocus, setDidFocus] = React.useState(false);
  const handleFocus = () => setDidFocus(true);
  const showFeedback =
    (!!didFocus && field.value.trim().length > 2) || meta.touched;

  return (
    <div
      className={`relative form-control ${
        showFeedback ? (meta.error ? 'invalid' : 'valid') : ''
      }`}
    >
      <input
        className="bg-[#F8FAFD] border border-[#E8EAF2] rounded-sm w-full h-9 px-3 py-1 focus:border-blue text-sm"
        {...props}
        {...field}
        aria-describedby={`${props.id}-feedback ${props.id}-help`}
        onFocus={handleFocus}
      />
        <div className="absolute left-0 top-[34px]">
        {showFeedback ? (
          <div
            id={`${props.id}-feedback`}
            aria-live="polite"
            className="feedback text-xs leading-[18px] text-red"
          >
            {meta.error ? meta.error : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function EntryForm(){
  const reCaptchaRef=useRef(null)
  const [backupTip,setBackupTip]=useState('')
  const [submitStatus,setSubmitStatus]=useState(false)
  const [submitText,setSubmitText]=useState('Explore CocoFax')
  const [loading,setLoading]=useState(false)

  const formik = useFormik({
    initialValues: {
      fname: '',
      lname: '',
      email: '',
      phone: '',
      comName: '',
      job: '',
      industry: '',
      comSize: '',
      budget: '',
      country: '',
    },
    onSubmit: async (values,{resetForm}) => {
      reCaptchaRef.current.executeAsync().then(token => {
        let obj = {
          firstName: values.fname,
          lastName: values.lname,
          email: values.email,
          phoneNumber: values.phone,
          company: values.comName,
          jobTitle: values.job,
          industry: values.industry,
          companySize: values.comSize,
          budget: values.budget,
          country: values.country,
          recaptcha: token
        }

        setLoading(true)
        setSubmitText("Loading...")
        fetch(API_PATH+'/api/potentialCustomer/addCustomer',{
          method:'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(obj)
        }).then((res)=>{

          if(res.ok){
            res.json().then(data=>{
              if(data.result===0){
                setSubmitStatus(true)
                setBackupTip('Submitted successfully!')
                resetForm({})
              }else{
                setSubmitStatus(false)
                setBackupTip('Submission failed!');

              }
            })
          }
          else{
            switch(res.status){
              case 401:
                setSubmitStatus(false)
                setBackupTip('Check your connection and try again!');
                break;
              case 400:
                setSubmitStatus(false)
                setBackupTip('Submission failed!');
                break;
              case 403:
                setSubmitStatus(false)
                setBackupTip('Access Denied!');
                break;
              default:
                setSubmitStatus(false)
                setBackupTip('Check your connection and try again!');
            }
          }
        })
        .catch((err)=>{
          setSubmitStatus(false)
          setBackupTip('Check your connection and try again!');
        }).then(()=>{
          setLoading(false)
          setSubmitText("Explore CocoFax")
        })
      })
    },
    validationSchema: Yup.object({
      fname: Yup.string()
        .max(128, 'Cannot exceed 128 characters')
        .required('Please enter first name'),
      lname: Yup.string()
      .max(128, 'Cannot exceed 128 characters')
      .required('Please enter last name'),
      email: Yup.string()
      .email('Please enter the correct email address')
      .max(256, 'Cannot exceed 256 characters')
      .required('Please enter your email address'),
      phone: Yup.string()
      .max(32, 'Cannot exceed 32 characters')
      .required('Please enter your phone number'),
      'comName': Yup.string()
      .max(200, 'Cannot exceed 200 characters')
      .required('Please enter company name'),
      job: Yup.string()
      .max(200, 'Cannot exceed 200 characters')
      .required('Please enter your job title'),
    }),
  })

  return (
    <FormikProvider value={formik}>
      <Form className="explore-form bg-white border border-gray-light rounded-[10px] overflow-hidden pt-[30px] pb-[38px] px-6 shadow-block w-[448px] lg:w-full flex-shrink-0 sm:flex-shrink">
        <div className="grid grid-cols-2 gap-4 pb-2 xs:grid-cols-1">
          <LiveFeedbackField type="text" id="fname" name="fname" placeholder="First Name" />
          <LiveFeedbackField type="text" id="lname" name="lname" placeholder="Last Name"/>
          <LiveFeedbackField type="email" id="email" name="email" placeholder="Email"/>
          <LiveFeedbackField type="tel" id="phone" name="phone" placeholder="Phone Number"/>
          <LiveFeedbackField type="text" id="comName" name="comName" placeholder="Company Name"/>
          <LiveFeedbackField type="text" id="job" name="job" placeholder="Job Title"/>
          <Field as="select" placeholder="Industry" id="industry" name="industry" required className="bg-[#F8FAFD] border border-[#E8EAF2] rounded-sm h-9 px-3 py-1 col-span-2 xs:col-span-1 focus:border-blue text-sm">
            <option value="" disabled="disabled" selected="selected">Industry</option>
            <option value="Insurance/Financial/Mortgage">Insurance/Financial/Mortgage</option>
            <option value="Government">Government</option>
            <option value="High Tech">High Tech</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Life Sciences">Life Sciences</option>
            <option value="Immigration">Immigration</option>
            <option value="Sports">Sports</option>
            <option value="Real Estate">Real Estate</option>
            <option value="Nonprofit">Nonprofit</option><option value="Construction">Construction</option><option value="Education">Education</option><option value="Vehicles">Vehicles</option><option value="Other">Other</option>
          </Field>
          <Field as="select" placeholder="Number of employees" id="comSize" name="comSize" required className="bg-[#F8FAFD] border border-[#E8EAF2] rounded-sm h-9 px-3 py-1 col-span-2 xs:col-span-1 focus:border-blue text-sm">
            <option value="" disabled="disabled" selected="selected">Number of employees</option>
            <option value="Solo">Solo</option>
            <option value="Small">0 - 50 employees</option>
            <option value="Medium_Small">50 - 100 employees</option>
            <option value="Medium">100 - 500 employees</option>
            <option value="Medium_Big">500 - 1000 employees</option>
            <option value="Big">1000 + employees</option>
          </Field>
          {/* <Field as="select" placeholder="Pages Faxed Per Month" id="budget" name="budget" required className="bg-[#F8FAFD] border border-[#E8EAF2] rounded-sm h-9 px-3 py-1 col-span-2 xs:col-span-1 focus:border-blue text-sm">
            <option value="" disabled="disabled" selected="selected">Pages Faxed Per Month</option>
            <option value="Under $1,000">Under $1,000</option>
            <option value="$1,001 to $10,000">$1,001 to $10,000</option>
            <option value="$10,001 to $50,000">$10,001 to $50,000</option>
            <option value="$50,001 or more">$50,001 or more</option>
          </Field> */}
          <Field type="number" placeholder="Pages Faxed Per Month" id="budget" name="budget" min="0" step="1" className="bg-[#F8FAFD] border border-[#E8EAF2] rounded-sm h-9 px-3 py-1 col-span-2 xs:col-span-1 focus:border-blue text-sm"/>
          <Field as="select" placeholder="Choose Country" id="country" name="country" required className="bg-[#F8FAFD] border border-[#E8EAF2] rounded-sm h-9 px-3 py-1 col-span-2 xs:col-span-1 focus:border-blue text-sm">
            <option value="" disabled="disabled" selected="selected">Choose Country</option>
            {
              countryList.map((elem,index)=>{
                return (
                  <option key={index} value={elem.name} selected={elem.code === "US"}>{elem.name}</option>
                )
              })
            }
          </Field>
          <Recaptcha sitekey="6LdWZ6UZAAAAAKl272O8vg_Sn-i0HZzlHDLXpoVm" ref={reCaptchaRef} size="invisible" />
        </div>
        <input type="submit" value={submitText} value="Explore CocoFax" className={`${loading?'pointer-events-none opacity-50':''} bg-green hover:bg-hover-green shadow-greenButton text-white text-lg font-bold w-full h-12 rounded-sm`} />
        <div className="relative pt-9">
          <p className={`backup-tip absolute top-[7px] text-xs leading-5 ${submitStatus?'text-green':'text-red'}`}>{backupTip}</p>
          <p className="text-gray text-xs leading-5">By clicking Explore CocoFax you agree to receive marketing communications from us in accordance with our <a href='/privacy.html' target='_blank' className="underline hover:underline">Privacy Policy.</a></p>
        </div>
      </Form>
    </FormikProvider>
  )
}